import React, { useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { UserMinus } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { PUBLISHER_NAMES } from '@/graphql/account-signals-queries'
import { AUDIENCE_EXCLUSIONS } from '@/graphql/audience-exclusions-queries'
import { metaPublisherIdOf } from './Settings/LanguageSettings'
import {
    EXCLUSION_CREATION_NOTE,
    EXCLUSION_OPTIONS,
    exclusionKindsForObjective,
    exclusionStatusLine,
    getExclusionChoice,
} from '@/utils/audience-exclusions'

interface ExclusionSource {
    kind: string
    label: string
    retention_days: number
    status: string
    approximate_count_lower: number | null
    message: string | null
}

interface ExclusionSettingsProps {
    objective: string
    getStepContentData: (step: string) => any
    setStepContentData: (data: Record<string, unknown>, step: string) => void
}

/**
 * Campaign-level exclusions of the acquisition audiences. Shown only when the licence offers them for the
 * objective; an ad campaign being edited keeps the exclusions it was published with, so nothing is shown.
 * The status comes from reads only: nothing is created before the campaign is published. A choice carried
 * over from a duplicated campaign is turned off once the server reports that kind as not offered, so it is
 * never sent without being shown.
 */
const ExclusionSettings: React.FC<ExclusionSettingsProps> = ({ objective, getStepContentData, setStepContentData }) => {
    const { id: campaignId } = useParams()
    const { connectedPublishers } = useMediaBuyingContext()
    const isEditMode = getStepContentData('prefetchedData')?.isEditMode === true
    const kinds = exclusionKindsForObjective(objective)
    const hasConnection = !!connectedPublishers && Object.keys(connectedPublishers).length > 0
    const enabled = !isEditMode && kinds.length > 0 && hasConnection && !!campaignId

    const publishersResult = useQuery(PUBLISHER_NAMES, { fetchPolicy: 'cache-first', skip: !enabled })
    const publisherId = metaPublisherIdOf(connectedPublishers, publishersResult.data?.getPublishersFromPostgres)
    const { data } = useQuery(AUDIENCE_EXCLUSIONS, {
        variables: { publisherId, campaignId, objective: String(objective || '').toLowerCase() },
        fetchPolicy: 'network-only',
        skip: !enabled || !publisherId,
    })

    const result = data?.audienceExclusions
    const sources: ExclusionSource[] = (result?.sources || []).filter((source: ExclusionSource) =>
        kinds.includes(source.kind),
    )

    const audienceTargetingData = getStepContentData('audienceTargeting') || {}
    const choice: Record<string, boolean> = getExclusionChoice(audienceTargetingData)
    const offeredKinds = result?.available ? sources.map((source) => source.kind) : []
    const notOffered = enabled && result ? kinds.filter((kind) => choice[kind] && !offeredKinds.includes(kind)) : []
    const notOfferedKey = notOffered.join(',')

    useEffect(() => {
        if (notOffered.length === 0) return
        const turnedOff = Object.fromEntries(notOffered.map((kind) => [kind, false]))
        setStepContentData({ exclusions: { ...choice, ...turnedOff } }, 'audienceTargeting')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notOfferedKey])

    if (!enabled || !result?.available || sources.length === 0) return null

    const setChoice = (kind: string, checked: boolean) => {
        setStepContentData({ exclusions: { ...choice, [kind]: checked } }, 'audienceTargeting')
    }

    return (
        <div
            className="tw:flex tw:gap-3 tw:flex-col tw:bg-secondary-background tw:rounded-xs tw:p-3"
            data-testid="exclusion-settings"
        >
            <div className="tw:flex tw:gap-1.5 tw:items-center">
                <UserMinus className="tw:w-6 tw:h-6 tw:text-gray-200" />
                <span className="tw:font-semibold">Exclusions</span>
                <span className="tw:text-sm tw:text-gray-200">(optional)</span>
            </div>
            {sources.map((source) => {
                const option = EXCLUSION_OPTIONS[source.kind as keyof typeof EXCLUSION_OPTIONS]
                const switchId = `exclusion-${source.kind}`
                return (
                    <div key={source.kind} className="tw:flex tw:flex-col tw:gap-1.5">
                        <div className="tw:flex tw:items-center tw:gap-2">
                            <Switch
                                id={switchId}
                                checked={choice[source.kind] === true}
                                onCheckedChange={(checked: boolean) => setChoice(source.kind, checked)}
                            />
                            <label htmlFor={switchId} className="tw:text-sm tw:font-medium tw:cursor-pointer">
                                {option.label}
                            </label>
                        </div>
                        <p className="tw:text-sm tw:text-gray-200">{option.description}</p>
                        <span className="tw:text-xs tw:text-gray-200" data-testid={`exclusion-status-${source.kind}`}>
                            {exclusionStatusLine(source)}
                        </span>
                    </div>
                )
            })}
            <p className="tw:text-xs tw:text-gray-200">{EXCLUSION_CREATION_NOTE}</p>
        </div>
    )
}

export default ExclusionSettings
